import { forwardRef, ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium",
    // §4: una sola curva, feedback immediato a 120ms.
    "transition-[background-color,border-color,color,box-shadow,transform] duration-fast ease-palazzo",
    "active:translate-y-px",
    // §8: focus visibile sempre, anello oro.
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-white/10 bg-transparent text-foreground hover:border-white/20 hover:bg-white/5",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-white/5 text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        /** CTA principale della schermata: oro pieno con bagliore (max 1 per vista). */
        gold: "bg-gradient-gold text-primary-foreground font-semibold shadow-sm hover:shadow-glow-primary",
        /** Azione secondaria dorata: velo d'oro su nero. */
        soft: "border border-primary/20 bg-primary/10 text-primary hover:border-primary/35 hover:bg-primary/15",
        /** Azione terziaria dorata, senza superficie. */
        ghostGold: "text-primary/75 hover:bg-primary/10 hover:text-primary",
      },
      // Sotto sm ogni taglia sale: il dito non ha la precisione del mouse.
      size: {
        default: "h-10 px-4 py-2 sm:h-9",
        xs: "h-7 rounded-md px-2 text-xs",
        sm: "h-9 rounded-md px-3 text-xs sm:h-8",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10 sm:h-9 sm:w-9",
        /** §8: target touch ≥40px per le icone-bottone su mobile. */
        "icon-lg": "h-10 w-10 rounded-md",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
